import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { Linkedin, Twitter, Instagram, Facebook, ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { COMPANY } from "../../lib/constants";
import { services } from "../../data/services";
import { toast } from "sonner";
import AnimatedLogo from "../shared/AnimatedLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success(t.contact.sent);
      setEmail("");
    } catch {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative border-t border-hair" data-testid="site-footer">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8 py-16 md:py-24">
        {/* Top brand row */}
        <div className="flex flex-col md:flex-row items-start justify-between gap-10 pb-12 border-b border-hair">
          <div className="max-w-md">
            <div className="flex items-center gap-3 mb-5">
              <AnimatedLogo size={48} />
              <div>
                <div className="text-xl font-semibold tracking-tight text-strong">Gulf TechLab</div>
                <div className="text-[11px] mono-label text-cyan-500/80">AI · COMMERCE · GROWTH</div>
              </div>
            </div>
            <p className="text-mid text-sm leading-relaxed mb-5">
              {lang === "en"
                ? "A premium technology partner helping Gulf businesses grow through AI, commerce, consultancy and performance."
                : "شريك تقني متميز يساعد أعمال الخليج على النمو بالذكاء الاصطناعي والتجارة والاستشارات والأداء."}
            </p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.footer.placeholder}
                data-testid="newsletter-email-input"
                className="flex-1 input-theme border rounded-lg px-4 py-3 text-sm outline-none transition"
                required
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-button"
                className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold text-sm px-5 rounded-lg transition inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {t.footer.subscribe} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
            <p className="text-xs text-soft mt-3">{t.footer.newsletter}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-10 md:gap-16 text-sm">
            <div>
              <h4 className="text-xs mono-label text-cyan-400 mb-4">{t.footer.solutions}</h4>
              <ul className="space-y-2.5">
                {services.slice(0, 6).map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="text-mid hover:text-cyan-300 transition">
                      {lang === "en" ? s.name_en : s.name_ar}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs mono-label text-cyan-400 mb-4">{t.footer.company}</h4>
              <ul className="space-y-2.5">
                <li><Link to="/about" className="text-mid hover:text-cyan-300">{t.nav.about}</Link></li>
                <li><Link to="/portfolio" className="text-mid hover:text-cyan-300">{t.nav.portfolio}</Link></li>
                <li><Link to="/industries" className="text-mid hover:text-cyan-300">{t.nav.industries}</Link></li>
                <li><Link to="/blog" className="text-mid hover:text-cyan-300">{t.nav.blog}</Link></li>
                <li><Link to="/contact" className="text-mid hover:text-cyan-300">{t.nav.contact}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs mono-label text-cyan-400 mb-4">{t.footer.legal}</h4>
              <ul className="space-y-2.5">
                <li><a href="#" className="text-mid hover:text-cyan-300">{t.footer.privacy}</a></li>
                <li><a href="#" className="text-mid hover:text-cyan-300">{t.footer.terms}</a></li>
                <li><a href="#" className="text-mid hover:text-cyan-300">{t.footer.cookies}</a></li>
              </ul>

              <h4 className="text-xs mono-label text-cyan-400 mt-7 mb-3">Contact</h4>
              <ul className="space-y-2 text-mid text-sm">
                <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-cyan-400/70" />{COMPANY.email}</li>
                <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-cyan-400/70" />{COMPANY.phone}</li>
                <li className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-cyan-400/70" />{COMPANY.address}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-soft">© {new Date().getFullYear()} Gulf TechLab. {t.footer.rights}</div>
          <div className="flex items-center gap-3">
            {[
              { Icon: Linkedin, href: COMPANY.social.linkedin, label: "linkedin" },
              { Icon: Twitter, href: COMPANY.social.twitter, label: "twitter" },
              { Icon: Instagram, href: COMPANY.social.instagram, label: "instagram" },
              { Icon: Facebook, href: COMPANY.social.facebook, label: "facebook" },
            ].map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={`social-${label}`}
                className="w-9 h-9 rounded-lg glass flex items-center justify-center text-mid hover:text-cyan-300 hover:border-cyan-400/40 transition"
                aria-label={label}
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
